import { defineAsyncComponent } from 'vue';
import { GlTooltipDirective, GlButton, GlDisclosureDropdown, GlDisclosureDropdownItem, GlDisclosureDropdownGroup, GlToastMixin } from '@gitlab/ui';
import { mapActions, mapState } from 'pinia';
import Api from '~/api';
import resolvedStatusMixin from '~/batch_comments/mixins/resolved_status';
import { createAlert } from '~/alert';
import { TYPE_ISSUE } from '~/issues/constants';
import { __, sprintf, s__ } from '~/locale';
import UserAccessRoleBadge from '~/vue_shared/components/user_access_role_badge.vue';
import { splitCamelCase, stripHtml, truncateByWords } from '~/lib/utils/text_utility';
import AbuseCategorySelector from '~/abuse_reports/components/abuse_category_selector.vue';
import { useNotes } from '~/notes/store/legacy_notes';
import Tracking from '~/tracking';
import glFeatureFlagsMixin from '~/vue_shared/mixins/gl_feature_flags_mixin';
import { convertToGraphQLId, getIdFromGraphQLId } from '~/graphql_shared/utils';
import { TYPENAME_NOTE } from '~/graphql_shared/constants';
import todoCreateMutation from '~/sidebar/queries/todo_create.mutation.graphql';
import todoMarkDoneMutation from '~/sidebar/queries/todo_mark_done.mutation.graphql';
import * as Sentry from '~/sentry/sentry_browser_wrapper';
import issueCommentTodosQuery from '../graphql/issue_comment_todos.query.graphql';
import mergeRequestCommentTodosQuery from '../graphql/merge_request_comment_todos.query.graphql';
import ReplyButton from './note_actions/reply_button.vue';
import TimelineEventButton from './note_actions/timeline_event_button.vue';
export default {
    i18n: {
        editCommentLabel: __('Edit comment'),
        deleteCommentLabel: __('Delete comment'),
        moreActionsLabel: __('More actions'),
        reportAbuse: __('Report abuse'),
        GENIE_CHAT_FEEDBACK_THANKS: s__('AI|Thanks for your feedback!'),
        editAriaLabel: __('Edit comment, @%{username}'),
        deleteAriaLabel: __('Delete comment, @%{username}'),
        replyAriaLabel: __('Reply to @%{username}'),
        addReactionAriaLabel: __('Add reaction to @%{username}'),
        moreActionsAriaLabel: __('More actions for @%{username}'),
        resolveAriaLabel: __('Resolve, @%{username}'),
        addCommentTodo: s__('Todos|Add a to-do'),
        markCommentTodoDone: s__('Todos|Mark as done'),
        commentTodoAdded: s__('Todos|To-do item added.'),
        commentTodoMarkedDone: s__('Todos|To-do item marked as done.'),
        commentTodoError: s__('Todos|Something went wrong while managing the to-do item.')
    },
    name: 'NoteActions',
    components: {
        AbuseCategorySelector,
        EmojiPicker: defineAsyncComponent(()=>import('~/emoji/components/picker.vue')),
        GlButton,
        GlDisclosureDropdown,
        GlDisclosureDropdownItem,
        GlDisclosureDropdownGroup,
        ReplyButton,
        TimelineEventButton,
        UserAccessRoleBadge,
        ViewSessionButton: defineAsyncComponent(()=>import('ee_component/ai/shared/widgets/view_session_button.vue')),
        DuoCodeReviewDepthBadge: defineAsyncComponent(()=>import('ee_component/notes/components/duo_code_review_depth_badge.vue'))
    },
    directives: {
        GlTooltip: GlTooltipDirective
    },
    mixins: [
        resolvedStatusMixin,
        Tracking.mixin(),
        GlToastMixin,
        glFeatureFlagsMixin()
    ],
    props: {
        author: {
            type: Object,
            required: true
        },
        authorId: {
            type: Number,
            required: true
        },
        noteId: {
            type: [
                String,
                Number
            ],
            required: true
        },
        noteUrl: {
            type: String,
            required: false,
            default: ''
        },
        notePreview: {
            type: String,
            required: false,
            default: ''
        },
        accessLevel: {
            type: String,
            required: false,
            default: ''
        },
        isAmazonQCodeReview: {
            type: Boolean,
            required: false,
            default: false
        },
        isAuthor: {
            type: Boolean,
            required: false,
            default: false
        },
        isContributor: {
            type: Boolean,
            required: false,
            default: false
        },
        noteableType: {
            type: String,
            required: false,
            default: ''
        },
        projectName: {
            type: String,
            required: false,
            default: ''
        },
        showReply: {
            type: Boolean,
            required: true
        },
        canEdit: {
            type: Boolean,
            required: false,
            default: false
        },
        canAwardEmoji: {
            type: Boolean,
            required: false,
            default: false
        },
        canDelete: {
            type: Boolean,
            required: false,
            default: false
        },
        canResolve: {
            type: Boolean,
            required: false,
            default: false
        },
        // eslint-disable-next-line vue/no-unused-properties -- resolvable is part of the component's public API.
        resolvable: {
            type: Boolean,
            required: false,
            default: false
        },
        isResolved: {
            type: Boolean,
            required: false,
            default: false
        },
        isResolving: {
            type: Boolean,
            required: false,
            default: false
        },
        // eslint-disable-next-line vue/no-unused-properties -- resolvedBy is part of the component's public API.
        resolvedBy: {
            type: Object,
            required: false,
            default: ()=>({})
        },
        canReportAsAbuse: {
            type: Boolean,
            required: true
        },
        // This can be undefined when `canAwardEmoji` is false
        awardPath: {
            type: String,
            required: false,
            default: ''
        },
        duoSessionId: {
            type: Number,
            required: false,
            default: null
        },
        duoCodeReviewDepth: {
            type: String,
            required: false,
            default: null
        }
    },
    emits: [
        'handle-delete',
        'handle-edit',
        'handle-resolve',
        'start-replying',
        'update-assignees'
    ],
    data () {
        return {
            isReportAbuseDrawerOpen: false,
            feedbackReceived: false,
            commentTodoId: null,
            todoLoading: false
        };
    },
    apollo: {
        commentTodoId: {
            query () {
                return this.commentTodosQuery;
            },
            variables () {
                return {
                    id: this.noteableGlobalId
                };
            },
            skip () {
                return !this.glFeatures.todoForComment;
            },
            update (data) {
                const todos = data.issuable?.currentUserTodos?.nodes || [];
                return todos.find((todo)=>getIdFromGraphQLId(todo.note?.id) === Number(this.noteId))?.id || null;
            },
            error (error) {
                Sentry.captureException(error);
            }
        }
    },
    computed: {
        ...mapState(useNotes, [
            'isPromoteCommentToTimelineEventInProgress',
            'getNoteableData',
            'canUserAddIncidentTimelineEvents'
        ]),
        notePreviewText () {
            if (!this.notePreview) return '';
            const strippedHtml = stripHtml(this.notePreview);
            return truncateByWords(strippedHtml);
        },
        ariaLabelNotePreview () {
            return this.notePreviewText ? `: '${this.notePreviewText}'` : '';
        },
        showDeleteAction () {
            return this.canDelete && !this.canReportAsAbuse && !this.noteUrl;
        },
        hasCommentTodo () {
            return Boolean(this.commentTodoId);
        },
        commentTodosQuery () {
            return this.isIssue ? issueCommentTodosQuery : mergeRequestCommentTodosQuery;
        },
        showCommentTodoMenuItem () {
            return this.glFeatures.todoForComment;
        },
        noteGlobalId () {
            return convertToGraphQLId(TYPENAME_NOTE, this.noteId);
        },
        noteableGlobalId () {
            return convertToGraphQLId(this.getNoteableData.noteableType, this.getNoteableData.id);
        },
        isUserAssigned () {
            return this.assignees && this.assignees.some(({ id })=>id === this.author.id);
        },
        displayAssignUserText () {
            return this.isUserAssigned ? __('Unassign comment author') : __('Assign to comment author');
        },
        targetType () {
            return this.getNoteableData.targetType;
        },
        noteableDisplayName () {
            return splitCamelCase(this.noteableType).toLowerCase();
        },
        assignees () {
            return this.getNoteableData.assignees || [];
        },
        isIssue () {
            return this.targetType === TYPE_ISSUE;
        },
        canAssign () {
            return this.getNoteableData.current_user?.can_set_issue_metadata && this.isIssue;
        },
        displayAuthorBadgeText () {
            return sprintf(__('This user is the author of this %{noteable}.'), {
                noteable: this.noteableDisplayName
            });
        },
        displayMemberBadgeText () {
            return sprintf(__('This user has the %{access} role in the %{name} project.'), {
                access: this.accessLevel.toLowerCase(),
                name: this.projectName
            });
        },
        displayContributorBadgeText () {
            return sprintf(__('This user has previously committed to the %{name} project.'), {
                name: this.projectName
            });
        },
        resolveIcon () {
            if (!this.isResolving) {
                return this.isResolved ? 'check-circle-filled' : 'check-circle';
            }
            return null;
        },
        feedbackModalComponent () {
            // Only load the EE component if this is an Amazon Q code review
            if (this.isAmazonQCodeReview) {
                return ()=>import('ee_component/ai/components/duo_chat_feedback_modal.vue');
            }
            return null;
        },
        replyAriaLabel () {
            return sprintf(this.$options.i18n.replyAriaLabel, {
                username: this.author.username
            }) + this.ariaLabelNotePreview;
        },
        editAriaLabel () {
            return sprintf(this.$options.i18n.editAriaLabel, {
                username: this.author.username
            }) + this.ariaLabelNotePreview;
        },
        deleteAriaLabel () {
            return sprintf(this.$options.i18n.deleteAriaLabel, {
                username: this.author.username
            }) + this.ariaLabelNotePreview;
        },
        addReactionAriaLabel () {
            return sprintf(this.$options.i18n.addReactionAriaLabel, {
                username: this.author.username
            }) + this.ariaLabelNotePreview;
        },
        moreActionsAriaLabel () {
            return sprintf(this.$options.i18n.moreActionsAriaLabel, {
                username: this.author.username
            }) + this.ariaLabelNotePreview;
        },
        resolveAriaLabel () {
            return [
                this.resolveButtonTitle,
                this.author.username && `@${this.author.username}`,
                this.ariaLabelNotePreview
            ].filter(Boolean).join(', ');
        }
    },
    methods: {
        ...mapActions(useNotes, [
            'toggleAwardRequest',
            'promoteCommentToTimelineEvent'
        ]),
        onEdit () {
            this.$emit('handle-edit');
        },
        onDelete () {
            this.$emit('handle-delete');
        },
        onResolve () {
            this.$emit('handle-resolve');
        },
        onAbuse () {
            this.toggleReportAbuseDrawer(true);
        },
        showFeedbackModal () {
            this.$refs.feedbackModal.show();
        },
        /**
     * Tracks feedback submitted for Amazon Q code reviews
     * @param {Object} options - The feedback options
     * @param {Array<string>} [options.feedbackOptions] - Array of selected feedback options (e.g. ['helpful', 'incorrect'])
     * @param {string} [options.extendedFeedback] - Additional text feedback provided by the user
     */ trackFeedback ({ feedbackOptions, extendedFeedback } = {}) {
            this.track('amazon_q_code_review_feedback', {
                action: 'amazon_q',
                label: 'code_review_feedback',
                property: feedbackOptions,
                extra: {
                    extendedFeedback,
                    note_id: this.noteId
                }
            });
            this.feedbackReceived = true;
        },
        onCopyUrl () {
            this.$toast.show(__('Link copied to clipboard.'));
        },
        handleAssigneeUpdate (assignees) {
            this.$emit('update-assignees', assignees);
        },
        assignUser () {
            let { assignees } = this;
            const { project_id, iid } = this.getNoteableData;
            if (this.isUserAssigned) {
                assignees = assignees.filter((assignee)=>assignee.id !== this.author.id);
            } else {
                assignees.push({
                    id: this.author.id
                });
            }
            if (this.targetType === TYPE_ISSUE) {
                Api.updateIssue(project_id, iid, {
                    assignee_ids: assignees.map((assignee)=>assignee.id)
                }).then(()=>this.handleAssigneeUpdate(assignees)).catch(()=>createAlert({
                        message: __('Something went wrong while updating assignees')
                    }));
            }
        },
        setAwardEmoji (awardName) {
            this.toggleAwardRequest({
                endpoint: this.awardPath,
                noteId: this.noteId,
                awardName
            });
        },
        toggleReportAbuseDrawer (isOpen) {
            this.isReportAbuseDrawerOpen = isOpen;
        },
        async onCreateCommentTodo () {
            const success = await this.runCommentTodoMutation(todoCreateMutation, {
                targetId: this.noteableGlobalId,
                noteId: this.noteGlobalId
            });
            if (success) this.$toast.show(this.$options.i18n.commentTodoAdded);
        },
        async onMarkCommentTodoDone () {
            const success = await this.runCommentTodoMutation(todoMarkDoneMutation, {
                id: this.commentTodoId
            });
            if (success) this.$toast.show(this.$options.i18n.commentTodoMarkedDone);
        },
        async runCommentTodoMutation (mutation, input) {
            if (this.todoLoading || this.$apollo.queries.commentTodoId.loading) return false;
            this.todoLoading = true;
            try {
                const { data } = await this.$apollo.mutate({
                    mutation,
                    variables: {
                        input
                    }
                });
                if (data.todoMutation.errors.length) throw new Error(data.todoMutation.errors[0]);
                await this.$apollo.query({
                    query: this.commentTodosQuery,
                    variables: {
                        id: this.noteableGlobalId
                    },
                    fetchPolicy: 'network-only',
                    context: {
                        queryDeduplication: false
                    }
                }).catch((error)=>Sentry.captureException(error));
                return true;
            } catch (error) {
                createAlert({
                    message: this.$options.i18n.commentTodoError,
                    captureError: true,
                    error
                });
                return false;
            } finally{
                this.todoLoading = false;
            }
        }
    }
};
